
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, Check, AlertCircle } from 'lucide-react';

interface CSVData {
  [key: string]: string | number;
}

const CSVUploader = () => {
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [csvData, setCsvData] = useState<CSVData[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const { toast } = useToast();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        toast({
          title: "Invalid file type",
          description: "Please upload a CSV file.",
          variant: "destructive",
        });
        return;
      }

      setFile(selectedFile);
      setFileName(selectedFile.name);
      toast({
        title: "File selected",
        description: `${selectedFile.name} has been selected.`,
      });
    }
  };

  const parseCSV = (text: string): { headers: string[], data: CSVData[] } => {
    const lines = text.split('\n');
    const headers = lines[0].split(',').map(header => header.trim());
    
    const data: CSVData[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim() === '') continue;
      
      const values = lines[i].split(',').map(value => value.trim());
      const row: CSVData = {};
      
      for (let j = 0; j < headers.length; j++) {
        const value = values[j] || '';
        // Try to convert to number if possible
        row[headers[j]] = isNaN(Number(value)) ? value : Number(value);
      }
      
      data.push(row);
    }
    
    return { headers, data };
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a CSV file to upload.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const { headers, data } = parseCSV(text);
          
          setHeaders(headers);
          setCsvData(data);
          
          toast({
            title: "CSV parsed successfully",
            description: `Processed ${data.length} rows of data.`,
          });
        } catch (error: any) {
          toast({
            title: "Error parsing CSV",
            description: error.message || "There was an error parsing the CSV file.",
            variant: "destructive",
          });
        } finally {
          setIsUploading(false);
        }
      };
      
      reader.onerror = () => {
        toast({
          title: "Error reading file",
          description: "There was an error reading the file.",
          variant: "destructive",
        });
        setIsUploading(false);
      };
      
      reader.readAsText(file);
    } catch (error: any) {
      toast({
        title: "Error uploading file",
        description: error.message || "There was an error uploading the file.",
        variant: "destructive",
      });
      setIsUploading(false);
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>CSV Uploader</CardTitle>
        <CardDescription>Upload a CSV file to view and process the data.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="flex-grow"
              id="csv-file"
            />
            <Button 
              onClick={handleUpload} 
              disabled={!file || isUploading}
              className="flex items-center gap-2"
            >
              {isUploading ? 'Processing...' : 'Upload'}
              {!isUploading && <Upload size={16} />}
            </Button>
          </div>
          
          {fileName && (
            <div className="flex items-center gap-2 text-sm">
              <Check size={16} className="text-green-500" />
              <span>Selected file: {fileName}</span>
            </div>
          )}
          
          {csvData.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-2">CSV Data Preview</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-muted/50">
                      {headers.map((header, index) => (
                        <th key={index} className="border px-4 py-2 text-left">{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.slice(0, 10).map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-b hover:bg-muted/30">
                        {headers.map((header, colIndex) => (
                          <td key={colIndex} className="border px-4 py-2">
                            {row[header] !== undefined ? String(row[header]) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {csvData.length > 10 && (
                  <div className="mt-2 text-muted-foreground text-sm">
                    Showing first 10 rows of {csvData.length} total rows.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CSVUploader;
